/**
 * Affiliate Marketplace Service
 * Handles fetching public affiliate products from the marketplace
 * Search logic matches Flutter app home_page.dart
 */

import { supabase } from '../supabaseClient';

export interface PublicProduct {
  product_id: number;
  product_name: string;
  price: number;
  main_images: string[];
  description: string;
  status: string;
  is_public_affiliate: boolean;
  default_commission_rate: number;
  default_commission_type: 'percentage' | 'fixed';
  seller_id: string;
  store_name: string;
  keywords: string[];
  created_at?: string; // 생성일 (정렬용)
  match_count?: number; // 키워드 매칭 개수
  // Affiliate settings
  affiliate_start_date?: string;
  affiliate_end_date?: string;
  affiliate_max_uses?: number;
  affiliate_current_uses?: number;
  seller: {
    id: string;
    store_name: string;
    store_description: string;
    profile_image?: string;
  };
}

export interface MarketplaceFilters {
  searchQuery?: string; // 일반 검색어 (상품명, 브랜드명)
  keywords?: string[]; // @ 키워드 (AND 조건)
  minCommissionPercentage?: number; // 퍼센트 최소값 (예: 10 = 10% 이상)
  maxCommissionPercentage?: number; // 퍼센트 최대값 (예: 20 = 20% 이하)
  minCommissionFixed?: number; // 고정금액 최소값 (예: 5000원 이상)
  commissionType?: 'percentage' | 'fixed' | 'all';
  sortBy?: 'newest' | 'commission' | 'price_low' | 'price_high';
}

/**
 * Fetch public affiliate products from marketplace
 * Search logic matches Flutter app (home_page.dart)
 * @param filters - Optional filters for searching and sorting
 * @param limit - Number of products to fetch (default: 50)
 * @param offset - Offset for pagination (default: 0)
 */
export async function getPublicAffiliateProducts(
  filters: MarketplaceFilters = {},
  limit: number = 50,
  offset: number = 0
): Promise<{ data: PublicProduct[] | null; error: any }> {
  try {
    console.log('=== 마켓플레이스 검색 시작 ===');
    console.log('검색어:', filters.searchQuery);
    console.log('키워드:', filters.keywords);
    console.log('수수료 타입:', filters.commissionType);
    console.log('필터:', filters);

    let allProducts: PublicProduct[] = [];
    const addedProductIds = new Set<number>(); // 중복 제거용

    // Base query
    // Note: is_public_affiliate, default_commission_rate, default_commission_type
    // are now managed in product_affiliate_settings table
    const baseSelect = `
      product_id,
      product_name,
      price,
      main_images,
      description,
      status,
      seller_id,
      store_name,
      keywords,
      created_at
    `;

    // Get product IDs and commission info from public affiliate settings
    // All marketplace products must have is_public = true in product_affiliate_settings
    // Filter by date: only show products within valid date range
    const now = new Date().toISOString();

    const { data: publicSettings, error: settingsError } = await supabase
      .from('product_affiliate_settings')
      .select('product_id, commission_rate, commission_type, start_date, end_date, max_uses, current_uses')
      .eq('is_public', true)
      .eq('status', 'approved');

    // 클라이언트 측에서 날짜 필터링 (Supabase OR 조건이 복잡해서)
    const validSettings = (publicSettings || []).filter(s => {
      // start_date가 없거나 현재보다 이전이어야 함
      const startOk = !s.start_date || new Date(s.start_date) <= new Date(now);
      // end_date가 없거나 현재보다 이후여야 함
      const endOk = !s.end_date || new Date(s.end_date) >= new Date(now);
      return startOk && endOk;
    });

    if (settingsError) {
      console.error('Error fetching public settings:', settingsError);
    }

    // 유효한 설정만 사용 (날짜 필터링 적용됨)
    const publicProductIds = new Set(validSettings.map(s => s.product_id));
    // Map product_id to commission settings and affiliate settings (this is now the source of truth)
    const publicCommissionMap = new Map(
      validSettings.map(s => [s.product_id, {
        rate: s.commission_rate,
        type: s.commission_type,
        start_date: s.start_date,
        end_date: s.end_date,
        max_uses: s.max_uses,
        current_uses: s.current_uses
      }])
    );
    console.log(`공개 설정된 상품 IDs: ${publicProductIds.size}개 (게시기간 필터 적용)`);

    // 1. 일반 검색어로 products 검색 (store_name, product_name, keywords에서 OR 검색)
    if (filters.searchQuery && filters.searchQuery.trim()) {
      console.log('일반 검색어로 products 검색');

      // OR 조건: store_name, product_name에서 ilike 검색, keywords에 contains 검색
      const searchTerm = filters.searchQuery.trim();
      const orCondition = `store_name.ilike.%${searchTerm}%,product_name.ilike.%${searchTerm}%,keywords.cs.{${searchTerm}}`;

      const { data: productsResponse, error: searchError } = await supabase
        .from('products')
        .select(baseSelect)
        .eq('status', 'active')
        .or(orCondition)
        .limit(500);

      if (searchError) throw searchError;

      // Filter products that have public affiliate settings
      const filteredProducts = (productsResponse || []).filter(p =>
        publicProductIds.has(p.product_id)
      );

      console.log(`일반 검색어 결과: ${filteredProducts.length}개 (공개 필터 적용 후)`);

      // 키워드 매칭 개수 계산
      const searchKeywords = filters.keywords?.map(k => k.toLowerCase()) || [];

      for (const product of filteredProducts) {
        const productId = product.product_id as number;
        if (!addedProductIds.has(productId)) {
          // 키워드 매칭 개수 계산
          let matchCount = 0;
          if (searchKeywords.length > 0) {
            const productKeywords = (product.keywords as string[] || []).map(k => k.toLowerCase());
            matchCount = searchKeywords.filter(keyword => productKeywords.includes(keyword)).length;
          }

          // 공개 설정의 커미션 정보 사용 (이제 이것이 유일한 소스)
          const commissionInfo = publicCommissionMap.get(productId);
          const commission_rate = commissionInfo?.rate || 10; // 기본값 10%
          const commission_type = commissionInfo?.type || 'percentage';

          allProducts.push({
            ...product,
            default_commission_rate: commission_rate,
            default_commission_type: commission_type,
            affiliate_start_date: commissionInfo?.start_date,
            affiliate_end_date: commissionInfo?.end_date,
            affiliate_max_uses: commissionInfo?.max_uses,
            affiliate_current_uses: commissionInfo?.current_uses,
            match_count: matchCount,
            seller: {
              id: product.seller_id as string,
              store_name: product.store_name as string,
              store_description: '',
            },
          } as PublicProduct);
          addedProductIds.add(productId);
        }
      }
    }

    // 2. 키워드로 products 검색 (추가 검색 - AND 조건)
    if (filters.keywords && filters.keywords.length > 0) {
      console.log('키워드로 products 추가 검색');
      const searchKeywords = filters.keywords.map(k => k.toLowerCase());

      const { data: keywordProducts, error: keywordError } = await supabase
        .from('products')
        .select(baseSelect)
        .eq('status', 'active')
        .limit(500);

      if (keywordError) throw keywordError;

      // 클라이언트 측에서 키워드 필터링 (모든 키워드 포함 - AND 조건)
      // AND 공개 설정 필터링
      const filteredByKeywords = (keywordProducts || []).filter((product) => {
        const productKeywords = (product.keywords as string[] || []).map(k => k.toLowerCase());
        const hasAllKeywords = searchKeywords.every(keyword => productKeywords.includes(keyword));
        const isPublic = publicProductIds.has(product.product_id);
        return hasAllKeywords && isPublic;
      });

      console.log(`키워드 검색 결과: ${filteredByKeywords.length}개 (공개 필터 적용 후)`);

      // 중복 제거하고 추가
      for (const product of filteredByKeywords) {
        const productId = product.product_id as number;
        if (!addedProductIds.has(productId)) {
          const productKeywords = (product.keywords as string[] || []).map(k => k.toLowerCase());
          const matchCount = searchKeywords.filter(keyword => productKeywords.includes(keyword)).length;

          // 공개 설정의 커미션 정보 사용 (이제 이것이 유일한 소스)
          const commissionInfo = publicCommissionMap.get(productId);
          const commission_rate = commissionInfo?.rate || 10; // 기본값 10%
          const commission_type = commissionInfo?.type || 'percentage';

          allProducts.push({
            ...product,
            default_commission_rate: commission_rate,
            default_commission_type: commission_type,
            affiliate_start_date: commissionInfo?.start_date,
            affiliate_end_date: commissionInfo?.end_date,
            affiliate_max_uses: commissionInfo?.max_uses,
            affiliate_current_uses: commissionInfo?.current_uses,
            match_count: matchCount,
            seller: {
              id: product.seller_id as string,
              store_name: product.store_name as string,
              store_description: '',
            },
          } as PublicProduct);
          addedProductIds.add(productId);
        }
      }
    }

    // 3. 검색어도 키워드도 없으면 전체 상품 가져오기
    if ((!filters.searchQuery || !filters.searchQuery.trim()) &&
        (!filters.keywords || filters.keywords.length === 0)) {
      console.log('검색어 없음 - 전체 공개 상품 조회');

      const { data: allProductsResponse, error: allError } = await supabase
        .from('products')
        .select(baseSelect)
        .eq('status', 'active')
        .limit(500);

      if (allError) throw allError;

      // Filter products that have public affiliate settings
      const filteredProducts = (allProductsResponse || []).filter(p =>
        publicProductIds.has(p.product_id)
      );

      allProducts = filteredProducts.map(p => {
        const productId = p.product_id as number;
        // 공개 설정의 커미션 정보 사용 (이제 이것이 유일한 소스)
        const commissionInfo = publicCommissionMap.get(productId);
        const commission_rate = commissionInfo?.rate || 10; // 기본값 10%
        const commission_type = commissionInfo?.type || 'percentage';

        return {
          ...p,
          default_commission_rate: commission_rate,
          default_commission_type: commission_type,
          affiliate_start_date: commissionInfo?.start_date,
          affiliate_end_date: commissionInfo?.end_date,
          affiliate_max_uses: commissionInfo?.max_uses,
          affiliate_current_uses: commissionInfo?.current_uses,
          match_count: 0,
          seller: {
            id: p.seller_id as string,
            store_name: p.store_name as string,
            store_description: '',
          },
        } as PublicProduct;
      });

      console.log(`전체 공개 상품: ${allProducts.length}개 (공개 필터 적용 후)`);
    }

    console.log(`총 상품 결과: ${allProducts.length}개 (중복 제거 후)`);

    // 4. 수수료 타입 필터 적용
    if (filters.commissionType && filters.commissionType !== 'all') {
      allProducts = allProducts.filter(p => p.default_commission_type === filters.commissionType);
      console.log(`수수료 타입 필터 후: ${allProducts.length}개`);
    }

    // 5. 수수료 금액 필터 적용
    // 퍼센트 타입: minCommissionPercentage ~ maxCommissionPercentage
    if (filters.minCommissionPercentage !== undefined) {
      allProducts = allProducts.filter(p => {
        if (p.default_commission_type === 'percentage') {
          return p.default_commission_rate >= filters.minCommissionPercentage!;
        }
        return true; // 고정금액은 퍼센트 필터 통과
      });
      console.log(`최소 수수료(%) 필터 후: ${allProducts.length}개`);
    }

    if (filters.maxCommissionPercentage !== undefined) {
      allProducts = allProducts.filter(p => {
        if (p.default_commission_type === 'percentage') {
          return p.default_commission_rate <= filters.maxCommissionPercentage!;
        }
        return true; // 고정금액은 퍼센트 필터 통과
      });
      console.log(`최대 수수료(%) 필터 후: ${allProducts.length}개`);
    }

    // 고정금액 타입: minCommissionFixed 이상
    if (filters.minCommissionFixed !== undefined) {
      allProducts = allProducts.filter(p => {
        if (p.default_commission_type === 'fixed') {
          return p.default_commission_rate >= filters.minCommissionFixed!;
        }
        return true; // 퍼센트는 고정금액 필터 통과
      });
      console.log(`최소 수수료(고정) 필터 후: ${allProducts.length}개`);
    }

    // 6. 정렬 적용
    switch (filters.sortBy) {
      case 'newest':
        allProducts.sort((a, b) => {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          return dateB - dateA;
        });
        break;
      case 'commission':
        allProducts.sort((a, b) => b.default_commission_rate - a.default_commission_rate);
        break;
      case 'price_low':
        allProducts.sort((a, b) => a.price - b.price);
        break;
      case 'price_high':
        allProducts.sort((a, b) => b.price - a.price);
        break;
      default:
        // 키워드 매칭 개수 순으로 정렬 (많이 매칭된 것 우선)
        allProducts.sort((a, b) => (b.match_count || 0) - (a.match_count || 0));
    }

    // 7. 페이지네이션 적용
    const paginatedProducts = allProducts.slice(offset, offset + limit);

    console.log(`페이지네이션 후: ${paginatedProducts.length}개`);

    if (paginatedProducts.length === 0) {
      return { data: [], error: null };
    }

    // 8. Seller 정보 가져오기
    const sellerIds = Array.from(new Set(paginatedProducts.map((p) => p.seller_id)));

    const { data: sellers, error: sellersError } = await supabase
      .from('seller_profile_public')
      .select('id, store_name, store_description, profile_image')
      .in('id', sellerIds);

    if (sellersError) {
      console.error('Error fetching sellers:', sellersError);
      // Continue without seller info rather than failing
    }

    // Map sellers by ID
    const sellersMap = new Map(
      (sellers || []).map((s) => [s.id, s])
    );

    // Combine products with seller info
    const combinedData: PublicProduct[] = paginatedProducts.map((product) => ({
      ...product,
      seller: sellersMap.get(product.seller_id) || {
        id: product.seller_id,
        store_name: product.store_name || '알 수 없음',
        store_description: '',
      },
    }));

    console.log(`최종 결과: ${combinedData.length}개`);

    return { data: combinedData, error: null };
  } catch (err) {
    console.error('Unexpected error in getPublicAffiliateProducts:', err);
    return { data: null, error: err };
  }
}

/**
 * Get a single product by ID with seller info
 * @param productId - The product ID to fetch
 */
export async function getProductById(
  productId: number
): Promise<{ data: PublicProduct | null; error: any }> {
  try {
    const { data: product, error: productError } = await supabase
      .from('products')
      .select(`
        product_id,
        product_name,
        price,
        main_images,
        description,
        status,
        is_public_affiliate,
        default_commission_rate,
        default_commission_type,
        seller_id,
        store_name,
        keywords
      `)
      .eq('product_id', productId)
      .single();

    if (productError) {
      console.error('Error fetching product:', productError);
      return { data: null, error: productError };
    }

    // Fetch seller info
    const { data: seller, error: sellerError } = await supabase
      .from('seller_profile_public')
      .select('id, store_name, store_description, profile_image')
      .eq('id', product.seller_id)
      .single();

    if (sellerError) {
      console.error('Error fetching seller:', sellerError);
    }

    const combinedData: PublicProduct = {
      ...product,
      seller: seller || {
        id: product.seller_id,
        store_name: product.store_name || '알 수 없음',
        store_description: '',
      },
    };

    return { data: combinedData, error: null };
  } catch (err) {
    console.error('Unexpected error in getProductById:', err);
    return { data: null, error: err };
  }
}

/**
 * Check if a creator has already requested access to a product
 * @param creatorId - The creator's seller_profile ID
 * @param productId - The product ID
 */
export async function hasRequestedProduct(
  creatorId: string,
  productId: number
): Promise<{ hasRequested: boolean; error: any }> {
  try {
    const { data, error } = await supabase
      .from('affiliate_product_requests')
      .select('id')
      .eq('creator_id', creatorId)
      .eq('product_id', productId)
      .in('status', ['pending', 'approved'])
      .limit(1);

    if (error) {
      console.error('Error checking request status:', error);
      return { hasRequested: false, error };
    }

    return { hasRequested: (data && data.length > 0), error: null };
  } catch (err) {
    console.error('Unexpected error in hasRequestedProduct:', err);
    return { hasRequested: false, error: err };
  }
}
